/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// utils
// material
import { Button, displayModal } from '@insticator/insticator-ui';
import Space from 'material/space/space'
// content
// styles
import injectSheet from "react-jss";
import styles from './addproductgroupmodal_styles';
import { setTempProductManagerConfig } from 'state/slices/productmanager';
import GroupName from '../productgroupv2/groupname/groupname';
import Placement from '../productgroupv2/placement/placement';
import Products from '../productgroupv2/products/products';
import Devices from '../productgroupv2/devices/devices';
import PreviewConfig from 'material/previewconfig/previewconfig';
import Preview from '../productgroupv2/preview/preview';

/* ========== ~~~~~~~~~~ MODERATION CONFIG PAGE ~~~~~~~~~~ ========== */
const AddProductGroupModal  = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { tempProductManagerConfig } = useSelector(state => state.productManager)
  const { siteUUID } = useSelector(state => state.session);

  const {  onCancel, onSave } = props

  const [isProcessing, setIsProcessing] = useState(false)

  const handleCancel = () => {
    // dispatch(displayModal(false))
    onCancel?.()
  }

  const {
    account: {
      accountProducts: approvedAccountProducts
    }
  } = useSelector((state) => state)

  const commentingEnabled = approvedAccountProducts.some(product => product === 'COMMENTING_V2')
  const trendingNowEnabled = approvedAccountProducts.some(product => product === 'TRENDING_NOW_V2')
  const articleSentimentEnabled = approvedAccountProducts.some(product => product === 'ARTICLE_SENTIMENT_V2')

  const defaultProductGroup =  {
    groupId: null,
    groupOrder: null,
    draft: {
      groupEnabled: true,
      infiniteScrollEnabled: false,
      label: "New Group",
      showCommenting: commentingEnabled,
      commentingOrder: 1,
      showTrendingNow: trendingNowEnabled, 
      trendingNowOrder: 2,
      showArticleSentiment: articleSentimentEnabled,
      articleSentimentOrder: 3,
      dynamicPlacement: true,
      selectorMode: "CSS", 
      selector: "",
      urlFilter: "",
      deviceType: 'ALL',
      layout: 'INLINE',
      trigger: 'NONE'
    },
    current: {
      groupEnabled: false,
      infiniteScrollEnabled: false,
      label: null, 
      showCommenting: commentingEnabled,
      commentingOrder: 1,
      showTrendingNow: trendingNowEnabled, 
      trendingNowOrder: 2,
      showArticleSentiment: articleSentimentEnabled,
      articleSentimentOrder: 3,
      dynamicPlacement: true,
      selectorMode: "CSS",
      selector: "",
      urlFilter: "",
      deviceType: 'ALL',
      layout: 'INLINE',
      trigger: 'NONE'
    }
  }

  const defaultControlBarConfig =  {
    draft: {
        mobile: {
            uuid: null,
            state: "DRAFT",
            deviceType: "MOBILE",
            siteUuid: siteUUID,
            primaryColor: "#061240",
            enableMonochrome: false,
            alignment: "RIGHT",
            placement: "BOTTOM"
        },
        desktop: {
            uuid: null,
            state: "DRAFT",
            deviceType: "DESKTOP",
            siteUuid: siteUUID,
            primaryColor: "#016240",
            enableMonochrome: false,
            alignment: "TOP",
            placement: "LEFT_EDGE"
        }
    },
    current: {
        mobile: {
            uuid: null,
            state: "LIVE",
            deviceType: "MOBILE",
            siteUuid: siteUUID,
            primaryColor: "#061240",
            enableMonochrome: false,
            alignment: "RIGHT",
            placement: "BOTTOM"
        },
        desktop: {
            uuid: null,
            state: "LIVE",
            deviceType: "DESKTOP",
            siteUuid: siteUUID,
            primaryColor: "#016240",
            enableMonochrome: false,
            alignment: "TOP",
            placement: "LEFT_EDGE"
        }
    }
  }

  const [group, setGroup] = useState(defaultProductGroup)

  const handleProductGroupChange = (property, value) => {
    const newProperty = {
      ...group,
    }
    newProperty['draft'][property] = value

     // Set to hardcode when infinite scroll is enabled
     if(property === 'infiniteScrollEnabled' && value === true) {
      newProperty['draft']['dynamicPlacement'] = false 
    }

    if(property === 'infiniteScrollEnabled' && value === false) {
      newProperty['draft']['dynamicPlacement'] = false 
    }

    // if there's no product selected disable group 
    if(newProperty['draft']['showCommenting'] === false && 
      newProperty['draft']['showTrendingNow'] === false && 
      newProperty['draft']['showArticleSentiment'] === false && 
      property !== 'groupEnabled') {
      newProperty['draft']['groupEnabled'] = false 
    }

    if(property === 'groupEnabled' && value === true) {
      newProperty['draft']['showCommenting'] = true
      newProperty['draft']['showTrendingNow'] = true
      newProperty['draft']['showArticleSentiment'] = true
    } 

    setGroup(newProperty)
  }

  const handleProductGroupPropertiesChange = (newPropertyValues) => {
    const newProperty = {
      ...group,
      ...newPropertyValues
    }
    newProperty['draft'] = {...newProperty['draft'],...newPropertyValues}
    if(newProperty['draft']['showCommenting'] === false && 
       newProperty['draft']['showTrendingNow'] === false &&
       newProperty['draft']['showArticleSentiment'] === false
    ) {
      newProperty['draft']['groupEnabled'] = false 
    }

    let newConfig = {...tempProductManagerConfig}

    if(Object.keys(newPropertyValues).includes('controlBarConfig')) {
      newConfig = {
        isControlBarChanged: true,
        ...tempProductManagerConfig, 
        controlBarConfig: {
          ...tempProductManagerConfig.controlBarConfig,
          draft: {
            ...tempProductManagerConfig.controlBarConfig?.draft || undefined,
            ...newPropertyValues.controlBarConfig
          }
        }
      }
      dispatch(setTempProductManagerConfig({config: newConfig, changes: 'groupConfigList'}))
    }
    setGroup(newProperty)
  }

  const controlBarConfig = useMemo(() => tempProductManagerConfig.controlBarConfig, [tempProductManagerConfig])

  const handleAddGroup = () => {
    setIsProcessing(true)
    dispatch(
      setTempProductManagerConfig({config: {
        ...tempProductManagerConfig,
        controlBarConfig,
        groupConfigList: [group, ...tempProductManagerConfig.groupConfigList]
      }, changes: 'groupConfigList'})
    )
    onSave?.()
  }

  const [products, selectProducts] = useState(['COMMENTING_V2','TRENDING_NOW_V2', 'ARTICLE_SENTIMENT_V2'])

  const groupNameExists = !!tempProductManagerConfig.groupConfigList.filter(existingGrp => existingGrp.draft.label === group.draft.label).length

  const groupNoDevice = group.draft.deviceType === null

  return (
    <div className={`addproductgroupmodal ${props.classes.addproductgroupmodal} ${group.draft.layout}`}>
      <h4>Add Product Group</h4>
      <Space klass={'configcontainer'} gap={24} direction={'row'}>
      <Space gap={20} direction={'column'}> 
        <GroupName isGroupNameExist={groupNameExists} label={group.draft.label} onChange={handleProductGroupChange} />
        <Devices deviceType={group.draft.deviceType} onChange={handleProductGroupChange} />
        <Placement 
          groupId={group.groupId} 
          {...group.draft} 
          products={products}
          onChange={handleProductGroupChange} 
          onFieldsChange={handleProductGroupPropertiesChange}
        />
        <Products 
          showCommenting={group.draft.showCommenting} 
          showTrendingNow={group.draft.showTrendingNow} 
          showArticleSentiment={group.draft.showArticleSentiment}
          onChange={handleProductGroupChange} 
          onFieldsChange={handleProductGroupPropertiesChange}  
          onProductOrderChange={products => selectProducts(products)}
          products={products}
        />
      </Space>
      <PreviewConfig>
        <Preview 
          layout={group.draft.layout || 'INLINE'} 
          trigger={group.draft.trigger || 'CUSTOM'} 
          products={products}
          showCommenting={group.draft.showCommenting} 
          showTrendingNow={group.draft.showTrendingNow} 
          showArticleSentiment={group.draft.showArticleSentiment}
          controlBarPlacement={controlBarConfig?.draft?.desktop?.placement}
          controlBarAlignment={controlBarConfig?.draft?.desktop?.alignment}
        />
      </PreviewConfig>
    </Space>
      <Space klass={'actionbuttons'} style={{marginTop: '12px'}} alignItems={'center'} justifyContent={'center'}>
        <Button text='Cancel' variant='none' color='light' handleClick={handleCancel} />
        <Button 
          text='Create' 
          variant='primary' 
          disabled={isProcessing || !group.draft.label || groupNameExists || groupNoDevice} 
          processingText={'Creating Group'} 
          processing={isProcessing} 
          handleClick={() => handleAddGroup()} 
        />
      </Space>
    </div>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default AddProductGroupModal